import { ERC1155_ABI } from 'constants/abi/erc1155-abi.constant';
import { NETWORK_SETTINGS } from 'constants/network.constant';
import { WEB3 } from 'constants/web3.constant';

import { BaseWeb3Service } from './base-web3.service';

/**
 * Service for handling ERC1155 NFT contract interactions
 * Manages permissions and transfers for ERC1155 tokens
 */
export class Erc1155Service extends BaseWeb3Service {
  private readonly erc1155HandlerAddress = NETWORK_SETTINGS.ERC1155_HANDLER.ADDRESS;

  /**
   * Checks if a wallet has approved the ERC1155 handler to manage their tokens
   */
  async checkPermission(walletAddress: string, assetAddress: string): Promise<boolean> {
    const contract = this.createContract(ERC1155_ABI, assetAddress);

    const hasPermission = await contract.methods.isApprovedForAll(walletAddress, this.erc1155HandlerAddress).call();

    return hasPermission;
  }

  /**
   * Grants permission to the ERC1155 handler to manage tokens
   */
  async grantPermission(walletAddress: string, assetAddress: string): Promise<void> {
    await this.updateNetwork();

    const gasPrice = await this.getGasPrice();
    const contract = this.createContract(ERC1155_ABI, assetAddress);

    await contract.methods.setApprovalForAll(this.erc1155HandlerAddress, true).send({
      from: walletAddress,
      gasPrice,
    });
  }

  /**
   * Transfers NFTs from one address to another
   */
  async safeTransferFrom(
    contractAddress: string,
    fromAddress: string,
    toAddress: string,
    tokenId: string,
    amount: string,
    callback: { onTransactionHash: (hash: string) => void; onConfirmation: () => void }
  ): Promise<void> {
    const contract = this.createContract(ERC1155_ABI, contractAddress);
    const gasPrice = await this.getGasPrice();

    await this.updateNetwork();

    await contract.methods
      .safeTransferFrom(fromAddress, toAddress, tokenId, amount, WEB3.nullStateHash)
      .send({ from: fromAddress, gasPrice })
      .on('transactionHash', (hash: string) => callback.onTransactionHash(hash))
      .on('confirmation', (blocks: number) => this.isConfirmed(blocks) && callback.onConfirmation());
  }
}
